import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, ShoppingCart, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatFiat, formatPct, FIATS } from "@/lib/api";

const POLL_MS = 30_000;

function PriceChart({ data, fxRate, fiat, color }) {
  const [hover, setHover] = useState(null);
  const padding = { l: 76, r: 16, t: 16, b: 32 };
  const width = 760;
  const height = 320;
  if (!data || data.length < 2) {
    return (
      <div className="h-[320px] grid place-items-center text-[#6E7587]">
        Chart data unavailable
      </div>
    );
  }
  const values = data.map((d) => d / fxRate);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const innerW = width - padding.l - padding.r;
  const innerH = height - padding.t - padding.b;
  const stepX = innerW / (values.length - 1);
  const points = values.map((v, i) => ({
    x: padding.l + i * stepX,
    y: padding.t + innerH - ((v - min) / range) * innerH,
    v,
    i,
  }));
  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPath =
    `M${padding.l},${padding.t + innerH} ` +
    points.map((p) => `L${p.x},${p.y}`).join(" ") +
    ` L${padding.l + innerW},${padding.t + innerH} Z`;

  const ticks = 5;
  const yTicks = Array.from({ length: ticks }, (_, i) => {
    const v = min + (range * i) / (ticks - 1);
    return { v, y: padding.t + innerH - ((v - min) / range) * innerH };
  });

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * width;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((x - padding.l) / stepX)));
    setHover(points[idx]);
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      className="select-none"
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
    >
      <defs>
        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {yTicks.map((t) => (
        <g key={`y-${t.y}`}>
          <line
            x1={padding.l}
            x2={width - padding.r}
            y1={t.y}
            y2={t.y}
            stroke="#1F232E"
            strokeWidth="1"
          />
          <text
            x={padding.l - 8}
            y={t.y + 4}
            fill="#6E7587"
            fontSize="10"
            fontFamily="IBM Plex Mono"
            textAnchor="end"
          >
            {t.v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </text>
        </g>
      ))}
      <path d={areaPath} fill="url(#areaGradient)" />
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        points={polyline}
      />
      {hover && (
        <g>
          <line
            x1={hover.x}
            x2={hover.x}
            y1={padding.t}
            y2={padding.t + innerH}
            stroke="#3A3F4D"
            strokeDasharray="2 3"
          />
          <circle cx={hover.x} cy={hover.y} r="4" fill={color} stroke="#0B0D12" strokeWidth="2" />
          <rect
            x={Math.min(hover.x + 8, width - 140)}
            y={Math.max(hover.y - 28, padding.t)}
            width="130"
            height="22"
            fill="#141720"
            stroke="#2A2E38"
          />
          <text
            x={Math.min(hover.x + 14, width - 134)}
            y={Math.max(hover.y - 12, padding.t + 16)}
            fill="#F4F1EB"
            fontSize="11"
            fontFamily="IBM Plex Mono"
          >
            {fiat} {hover.v.toLocaleString(undefined, { maximumFractionDigits: 4 })}
          </text>
        </g>
      )}
    </svg>
  );
}

export default function MarketDetail() {
  const { symbol } = useParams();
  const sym = (symbol || "").toUpperCase();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [fiat, setFiat] = useState(() => localStorage.getItem("orbital_market_fiat") || "USD");
  const [fxRates, setFxRates] = useState({ USD: 1, CAD: 0.73, EUR: 1.08 });
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      const [m, fx] = await Promise.all([
        api.get(`/markets/${sym}`),
        api.get("/fx-rates"),
      ]);
      setAsset(m.data);
      setFxRates(fx.data.rates);
      setUpdatedAt(new Date());
    } catch (err) {
      toast.error("Could not load asset");
      navigate("/app/markets");
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, POLL_MS);
    return () => clearInterval(i);
  }, [sym]);

  const setFiatPersist = (f) => {
    setFiat(f);
    localStorage.setItem("orbital_market_fiat", f);
  };

  const fxRate = fxRates[fiat] || 1;
  const priceInFiat = asset ? asset.price_usd / fxRate : 0;
  const up = (asset?.change_24h || 0) >= 0;
  const color = up ? "#7C9C69" : "#C75C5C";

  const { high, low } = useMemo(() => {
    if (!asset?.sparkline?.length) return { high: 0, low: 0 };
    return {
      high: Math.max(...asset.sparkline) / fxRate,
      low: Math.min(...asset.sparkline) / fxRate,
    };
  }, [asset, fxRate]);

  if (!asset) {
    return <div className="text-[#6E7587]">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/app/markets"
        data-testid="market-detail-back"
        className="inline-flex items-center gap-2 text-xs text-[#A3A9B8] hover:text-[#7C5CFF] transition-colors"
      >
        <ArrowLeft size={14} /> Back to markets
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          {asset.image && <img src={asset.image} alt="" className="w-12 h-12" />}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-bold">{asset.name}</h1>
              <span className="text-xs tracking-[0.2em] uppercase text-[#6E7587] font-mono">
                {asset.symbol}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7C9C69] live-dot"></span>
              <span className="text-[#A3A9B8]">Live · auto-refresh</span>
            </div>
          </div>
        </div>

        {/* Fiat toggle (per-page) */}
        <div className="flex gap-1 border border-[#2A2E38] rounded-sm overflow-hidden">
          {FIATS.map((f) => (
            <button
              key={f}
              data-testid={`market-detail-fiat-${f}`}
              onClick={() => setFiatPersist(f)}
              className={`px-4 py-2 text-xs font-mono transition-colors ${
                fiat === f
                  ? "bg-[#7C5CFF] text-white"
                  : "text-[#A3A9B8] hover:text-[#F4F1EB] hover:bg-[#1C202B]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Price + actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[#2A2E38] bg-[#141720] p-6 md:p-8">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B8]">
                Live price · {fiat}
              </div>
              <div
                data-testid="market-detail-price"
                className="font-mono text-5xl md:text-6xl font-medium mt-2 tracking-tight"
              >
                {formatFiat(priceInFiat, fiat, priceInFiat < 1 ? 6 : 2)}
              </div>
              <div className={`mt-2 text-sm font-mono inline-flex items-center gap-1 ${up ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}>
                {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {formatPct(asset.change_24h)} <span className="text-[#6E7587]">· 24h</span>
              </div>
            </div>
            <div className="text-xs text-[#6E7587] font-mono">
              Updated {updatedAt ? updatedAt.toLocaleTimeString() : "—"}
            </div>
          </div>

          <div className="mt-8">
            <PriceChart data={asset.sparkline} fxRate={fxRate} fiat={fiat} color={color} />
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2E38]">
            <div className="bg-[#0B0D12] p-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">7d high</div>
              <div className="font-mono text-sm mt-1">{formatFiat(high, fiat, high < 1 ? 6 : 2)}</div>
            </div>
            <div className="bg-[#0B0D12] p-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">7d low</div>
              <div className="font-mono text-sm mt-1">{formatFiat(low, fiat, low < 1 ? 6 : 2)}</div>
            </div>
            <div className="bg-[#0B0D12] p-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">Market cap</div>
              <div className="font-mono text-sm mt-1">
                {formatFiat(asset.market_cap / fxRate, fiat, 0)}
              </div>
            </div>
            <div className="bg-[#0B0D12] p-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">24h volume</div>
              <div className="font-mono text-sm mt-1">
                {formatFiat(asset.volume_24h / fxRate, fiat, 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="border border-[#2A2E38] bg-[#141720] p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Quick action</div>
            <h3 className="font-display text-xl font-bold mt-1">Trade {asset.symbol}</h3>
            <p className="text-xs text-[#A3A9B8] mt-2">
              Use your linked fiat or existing crypto holdings to enter or exit a position.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                data-testid="market-detail-buy"
                onClick={() => navigate("/app/trade", { state: { asset: asset.symbol, side: "buy" } })}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#7C9C69] text-white rounded-sm hover:bg-[#8FAF7B] transition-colors text-sm"
              >
                <ShoppingCart size={14} /> Buy {asset.symbol}
              </button>
              <button
                data-testid="market-detail-sell"
                onClick={() => navigate("/app/trade", { state: { asset: asset.symbol, side: "sell" } })}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#2A2E38] text-[#F4F1EB] rounded-sm hover:border-[#C75C5C] hover:text-[#C75C5C] transition-colors text-sm"
              >
                <Tag size={14} /> Sell {asset.symbol}
              </button>
            </div>
          </div>

          <div className="border border-[#2A2E38] bg-[#141720] p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Conversion table</div>
            <h3 className="font-display text-lg font-bold mt-1">1 {asset.symbol} =</h3>
            <div className="mt-3 space-y-2">
              {FIATS.map((f) => (
                <div key={f} className="flex items-center justify-between text-sm">
                  <span className="text-[#A3A9B8] font-mono">{f}</span>
                  <span className="font-mono">
                    {(asset.price_usd / (fxRates[f] || 1)).toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
